import url from 'url';
import { container } from 'op/container';
import update from 'immutability-helper';
import { connect } from 'react-redux';
import { goBack } from 'react-router-redux';
import { compose, withState, withHandlers, withProps } from 'recompose';
import { useSelector } from 'react';
import { withAxios } from 'op/components/auth';
import { withTypes, withKpiTypes } from 'op/components/lut';
import { withLanguageID } from 'op/components/locale';
import { withErrorHandling } from 'op/components/error';
import { withOnlineStatus } from 'op/components/offline';
import { selectSystemSettings } from '../../redux/reducers/system-settings/system-settings.selectors';

export const validateReport = (report, reportType, online, kpiTypes, systemSettings) => {
    if (!report) {
        return { ok: false, message: 'Report Is Undefined', field: 'timestamp' };
    }

    if (!report.timestamp) {
        return { ok: false, message: 'Report Date should not be empty', field: 'timestamp' };
    }

    if (!report.type) {
        return { ok: false, message: 'Report Type should be selected', field: 'type' };
    }

    if (!report.category) {
        return { ok: false, message: 'Report Subject should be selected', field: 'category' };
    }

    if (!!report.lastUpdateTime && (!reportType || ['score', 'execution_level'].includes(reportType.type) && !report[reportType.type])) {
        return { ok: false, message: 'Please select Execution Level / Score before submitting the form.' };
    }
    if (kpiTypes) {
        if (kpiTypes[report.category] && report.kpiMandatory) {
            if (kpiTypes[report.category].filter(x => !x.isexpired).length !== report.evaluations.length
            ||(report.evaluations.filter(x => x.execution_level==null).length>0 &&reportType.type=="execution_level")
            ||(report.evaluations.filter(x => x.score==null).length>0&&reportType.type=="score")) {
                return { ok: false, message: 'Please select Execution Level before submitting the form.' };
            }
        }
    }

    if (systemSettings && systemSettings.isRemarkForFailure && 
        report.execution_level < 30 && 
        (!report.remarks || !report.remarks.general)) {
        return { 
            ok: false, 
            message: 'Feedback / General Remarks is required when Execution Level is Fail', 
            field: 'remarks' 
        };
    }

    return {
        ok: true,
    };
};

export const withReportFetch = compose(
    withAxios,
    withLanguageID,
    withErrorHandling,
    withOnlineStatus,
    withKpiTypes,
    withProps(({ report, kpiTypes, types }) => ({
        kpiTypes: kpiTypes[report ? report.category : 0] || []
    })),
    withHandlers({
        fetchReports: ({ language=1, axios }) => async (person, top = 10, skip = 0) => {
            var person_id=person.person_id;
            const reqestURL = url.format({
                pathname: '/api/tasks/training_event_2_person',
                query: {person_id, language, skip, top }
            });
            
            const { data } = await axios.get(reqestURL);
            console.log(data);
            return data
                .map((raw) => container.resolve('transformers').report(raw))
                .map(report => update(report, { partial: { $set: true } }));
        },
        fetchReport: ({ language, dispatch, throwError, axios }) => async ({ id, person_id }) => {
            const { data: [response] } = await axios.get(url.format({
                pathname: '/api/tasks/training_person_event_details',
                query: { person_id, report_id: id, language }
            }));

            if (!response.taskInfo) {
                throwError({ message: "Report Unavailable", details: "Report may have been deleted" });
                return dispatch(goBack());
            }

            const report = container.resolve('transformers').report(response.taskInfo);
            return update(report, {
                evaluations: { $set: response.taskEvaluation.map(container.resolve('transformers').evaluation) },
                attachments: { $set: response.taskAttachment.map(container.resolve('transformers').attachment) }
            });
        }
    })
);

export const withReportLoading =
    connect(undefined, (dispatch) => ({
        addLoadedReports: (reports) =>
            dispatch(container.resolve('actions').add_reports(reports))
    }))

    export const withReportSaving = compose(
    withAxios,
    withLanguageID,
    withReportFetch,
    withErrorHandling,
    withHandlers({
        invokeChange: ({ report, setReport }) => (change) => {
            const withDirty = {
                ...change,
                dirty: { $set: [...(report.dirty || []), ...Object.keys(change)].filter((v, i, a) => a.indexOf(v) === i) }
            };
            setReport(update(report, withDirty));
        },
        saveReport: ({ axios, report, language, fetchReport, throwError, dispatch }) => async (completed) => {
            try {
                const { data: [rawReport] } = await axios.post('/api/tasks/task_save', {
                     languageID: language,
                    IsSimfoxData: 0,
                    ...container.resolve('transformers').report.reverse({
                        ...report,
                        completed,
                        id: isNaN(report.id) ? undefined : report.id
                    },103),
                });

                const updatedReport = container.resolve('transformers').report(rawReport);

                if (report.dirty && report.dirty.includes('evaluations')) {
                    await axios.post('/api/kpi/kpi_save_bulk',
                        report.evaluations
                            .map(evaluation => Object.assign(evaluation, { report_id: updatedReport.id ,person_id:updatedReport.hidden_person_id}))
                            .map(container.resolve('transformers').evaluation.reverse)
                    );
                    await axios.post('/api/Stp/saveSubEvaluationType', 
                        report.evaluations
                            .map(evaluation => Object.assign(evaluation, 
                                { 
                                    report_id: updatedReport.id,
                                    person_id:updatedReport.hidden_person_id,
                                    type: {
                                        id: evaluation.type.id
                                    },
                                    alert: evaluation.alert
                                }))
                    )
                }

                if (report.dirty && report.dirty.includes('signatures')) {
                    await axios.post('/api/images/save_signature', {
                        report_id: updatedReport.id,
                        SSOSigBase64: report.signatures.SSOSig,
                        ManagerSigBase64: report.signatures.ManagerSig,
                        EmpSigBase64: report.signatures.EmpSig,
                    });
                }

                return fetchReport({id:updatedReport.id,person_id:updatedReport.hidden_person_id});
            } catch (e) {
                throwError({
                    message: 'Error While Saving Report',
                    details: `
						${e.message};
						${e.stack}
					`,
                });
                throw e;
            }
        },
        deleteReport: ({ axios, report, throwError }) => async () => {
            try {
                return await axios.get(url.format({
                    pathname: '/api/tasks/task_delete',
                    query: { person_id: report.hidden_person_id, report_id: report.id }
                }));

            } catch (e) {
                throwError({
                    message: 'Error While Deleting Report',
                    details: `
						${e.message};
						${e.stack}
					`,
                });
            }
        },
        deleteReportFromPending: ({ axios, throwError }) => async (report) => {
            try {
                return await axios.get(url.format({
                    pathname: '/api/tasks/task_delete',
                    query: { person_id: report.hidden_person_id, report_id: report.id }
                }));

            } catch (e) {
                throwError({
                    message: 'Error While Deleting Report',
                    details: `
						${e.message};
						${e.stack}
					`,
                });
            }
        },
        downloadReport: ({ axios, report, throwError }) => async (report, person, language) => {
            try {
                /* var data={TaskInfo: {        
                    id:report.id, 
                    type:report.type,
                    category:report.category,
                    person_id:report.person_id,
                    languageID:language
            },
            Employee:{
                id:person.id,                    
                display:person. display,
                name:person.name,            
                units:person.units,
                jobs:person.jobs,
            }}; */
                report.languageID = language;
                var data = {
                    TaskInfo: report,
                    Employee: person,
                    TaskEvaluation: report.evaluations
                };
                return await axios.post('/api/tasks/getDownloadPdf', data, {
                    responseType: 'blob'
                });
            }
            catch (e) {
                throwError({
                    message: 'Error While getDownloadPdf',
                    details: `
                        ${e.message};
                        ${e.stack}
                    `,
                });
            }
        }
    }),
    withHandlers({
        handleKpiAdd: ({ report, invokeChange }) => async ({ kpi, type, value, remarks, alert }) => {
            const evalObject = {
                report_id: report.id,
                report_type: report.type.id,
                person_id: report.hidden_person_id,
                type: kpi,
                [type]: value,
                remarks,
                alert,
            };

            const evaluationIndex = report.evaluations.findIndex(evaluation => evaluation.type.id === kpi.id);
            if (evaluationIndex !== -1) {
                invokeChange({
                    evaluations: { [evaluationIndex]: { $set: evalObject } }
                });
            } else {
                invokeChange({
                    evaluations: { $push: [evalObject] }
                });
            }
        },
    }),
)

const mapStateToProps = (state) => ({
    systemSettings: selectSystemSettings(state)
});

export const withReportValidation = compose(
    connect(mapStateToProps),
    withTypes,
    withOnlineStatus,
    withKpiTypes,
    withProps(({ report, types, online, kpiTypes, systemSettings }) => ({
        validation: validateReport(report, report && report.type && types.find(type => report.type.id === type.id), online, kpiTypes, systemSettings)
    }))
);
